<template>
  <div class="category-selector">
    <div class="flex gap-2">
      <div class="flex-1">
        <div class="custom-dropdown" :class="{ 'is-open': isDropdownOpen }">
          <div 
            class="dropdown-trigger"
            @click.stop="toggleDropdown"
          >
            <span v-if="selectedCategory" class="selected-category">
              <span 
                class="category-color-dot"
                :style="{ backgroundColor: selectedCategory.color }"
              ></span>
              {{ selectedCategory.name }}
            </span>
            <span v-else class="placeholder">
              {{ $t('BUSINESS_DATA.CATEGORY.SELECT_CATEGORY') }}
            </span>
            <fluent-icon 
              icon="chevron-down" 
              size="16" 
              class="dropdown-arrow"
              :class="{ 'is-rotated': isDropdownOpen }"
            />
          </div>
          
          <div v-if="isDropdownOpen" class="dropdown-menu" @click.stop>
            <div 
              class="dropdown-item"
              @click.stop="selectCategory(null)"
            >
              {{ $t('BUSINESS_DATA.CATEGORY.SELECT_CATEGORY') }}
            </div>
            <div
              v-for="category in categories"
              :key="category.id"
              class="dropdown-item"
              @click.stop="selectCategory(category)"
            >
              <span 
                class="category-color-dot"
                :style="{ backgroundColor: category.color }"
              ></span>
              {{ category.name }}
            </div>
          </div>
        </div>
      </div>
      
      <woot-button
        type="button"
        variant="clear"
        size="small"
        @click="showCreateModal = true"
      >
        <fluent-icon icon="add" size="16" />
        {{ $t('BUSINESS_DATA.CATEGORY.ADD_NEW') }}
      </woot-button>
    </div>
    
    <!-- Selected category preview -->
    <div v-if="selectedCategory" class="category-preview">
      <span
        class="category-badge"
        :style="{ 
          backgroundColor: selectedCategory.color, 
          color: getTextColor(selectedCategory.color) 
        }"
      >
        {{ selectedCategory.name }}
      </span>
    </div>
    
    <!-- Create category modal -->
    <CategoryModal
      :show="showCreateModal"
      :category-type="categoryType"
      @close="showCreateModal = false"
      @created="onCategoryCreated"
    />
  </div>
</template>

<script>
import CategoryModal from './CategoryModal.vue';

export default {
  name: 'CategorySelector',
  components: { CategoryModal },
  
  props: {
    // Vue 2 v-model
    value: {
      type: [String, Number],
      default: null,
    },
    // Vue 3 v-model
    modelValue: {
      type: [String, Number],
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    categoryType: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  
  data() {
    return {
      showCreateModal: false,
      isDropdownOpen: false,
      localSelectedId: null,
    };
  },
  
  computed: {
    selectedCategoryId() {
      return this.localSelectedId;
    },
    
    selectedCategory() {
      if (!this.selectedCategoryId) return null;
      return this.categories.find(cat => cat.id == this.selectedCategoryId);
    },
  },
  
  watch: {
    categories: {
      immediate: true,
      handler(newCategories) {
        console.log('CategorySelector received categories:', newCategories);
      },
    },
  },
  
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.localSelectedId = newVal;
      },
    },
    modelValue(newVal) {
      if (this.value === undefined) {
        this.localSelectedId = newVal;
      }
    },
  },

  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    
    selectCategory(category) {
      const newValue = category ? category.id : null;
      this.localSelectedId = newValue;
      // Emit for both Vue 2 and Vue 3 consumers
      this.$emit('input', newValue);
      this.$emit('update:modelValue', newValue);
      this.isDropdownOpen = false;
    },
    
    getTextColor(backgroundColor) {
      if (!backgroundColor) return '#000000';
      
      // Convert hex to RGB
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Calculate luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    },
    
    onCategoryCreated(newCategory) {
      // Add new category to the list
      this.$emit('category-created', newCategory);
      // Select the new category
      this.selectedCategoryId = newCategory.id;
      this.showCreateModal = false;
    },
  },
  
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.isDropdownOpen = false;
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.category-selector {
  .custom-dropdown {
    position: relative;
    width: 100%;
    
    .dropdown-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--space-small);
      border: 1px solid var(--color-border);
      border-radius: var(--border-radius-normal);
      background-color: white;
      cursor: pointer;
      transition: border-color 0.2s ease;
      
      &:hover {
        border-color: var(--color-primary);
      }
      
      .selected-category,
      .placeholder {
        display: flex;
        align-items: center;
        gap: var(--space-smaller);
        font-size: var(--font-size-default);
      }
      
      .placeholder {
        color: var(--color-text-light);
      }
      
      .category-color-dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        display: inline-block;
        flex-shrink: 0;
      }
      
      .dropdown-arrow {
        transition: transform 0.2s ease;
        color: var(--color-text-light);
        
        &.is-rotated {
          transform: rotate(180deg);
        }
      }
    }
    
    .dropdown-menu {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: white;
      border: 1px solid var(--color-border);
      border-radius: var(--border-radius-normal);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      z-index: 1000;
      max-height: 200px;
      overflow-y: auto;
      
      .dropdown-item {
        display: flex;
        align-items: center;
        gap: var(--space-smaller);
        padding: var(--space-small);
        cursor: pointer;
        font-size: var(--font-size-default);
        transition: background-color 0.2s ease;
        
        &:hover {
          background-color: var(--color-background-light);
        }
        
        &:first-child {
          border-top-left-radius: var(--border-radius-normal);
          border-top-right-radius: var(--border-radius-normal);
        }
        
        &:last-child {
          border-bottom-left-radius: var(--border-radius-normal);
          border-bottom-right-radius: var(--border-radius-normal);
        }
        
        .category-color-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          display: inline-block;
          flex-shrink: 0;
        }
      }
    }
    
    &.is-open .dropdown-trigger {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 1px var(--color-primary);
    }
  }
}

.category-preview {
  margin-top: var(--space-small);
  padding: var(--space-small);
  background: var(--color-background-light);
  border-radius: var(--border-radius-normal);
  border: 1px solid var(--color-border-light);
}

.category-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  text-align: center;
  min-width: 80px;
}
</style>
